import { CheckIcon } from '@/components/icons';
import type { Step } from '@/types/types';
import { classNames } from '@/utils/classNames';
import { Fragment } from 'react';
import styles from './BookingStepper.module.css';

const STEPS: { step: Step; label: string }[] = [
  { step: 1, label: 'Enter Trip Details' },
  { step: 2, label: 'Recommended Ski Pass' },
  { step: 3, label: 'Booking Details' },
  { step: 4, label: 'Payment' },
];

type StepState = 'completed' | 'active' | 'incomplete';

const getStepState = (step: number, currentStep: number): StepState => {
  if (step < currentStep) return 'completed';
  if (step === currentStep) return 'active';
  return 'incomplete';
};

type BookingStepperProps = {
  currentStep: Step;
  onStepClick?: (step: Step) => void;
};

const StepCircle = ({
  step,
  state,
  small,
}: {
  step: number;
  state: StepState;
  small?: boolean;
}) => (
  <div
    className={classNames(
      styles.stepCircle,
      styles[`circle_${state}`],
      small && styles.stepCircleSmall
    )}
  >
    {state === 'completed' ? (
      <CheckIcon className={classNames(styles.checkIcon, small && styles.checkIconSmall)} />
    ) : (
      step
    )}
  </div>
);

export const BookingStepper = ({ currentStep, onStepClick }: BookingStepperProps) => (
  <nav className={styles.stepper}>
    <div className={styles.desktopStepper}>
      {STEPS.map(({ step, label }, index) => {
        const state = getStepState(step, currentStep);
        const isLast = index === STEPS.length - 1;
        const isClickable = state === 'completed' && onStepClick != null;
        const itemClass = classNames(
          styles.stepItem,
          styles[`item_${state}`],
          isClickable && styles.clickable
        );
        const content = (
          <>
            <StepCircle step={step} state={state} />
            <span
              className={classNames(styles.stepLabel, state === 'active' && styles.stepLabelActive)}
            >
              {label}
            </span>
          </>
        );
        return (
          <Fragment key={step}>
            {isClickable ? (
              <button
                type="button"
                className={itemClass}
                onClick={() => {
                  onStepClick(step);
                }}
              >
                {content}
              </button>
            ) : (
              <div className={itemClass}>{content}</div>
            )}
            {!isLast && <div className={styles.stepDivider} aria-hidden />}
          </Fragment>
        );
      })}
    </div>

    <div className={styles.mobileStepperHeader}>
      <p className={styles.mobileStepperHeaderStep}>
        Step {currentStep} of {STEPS.length}
      </p>
      <p className={styles.mobileStepperHeaderTitle}>
        {STEPS.find(({ step }) => step === currentStep)?.label}
      </p>
    </div>

    <div className={styles.mobileStepper}>
      {STEPS.map(({ step }, index) => {
        const state = getStepState(step, currentStep);
        const isLast = index === STEPS.length - 1;
        const isClickable = state === 'completed' && onStepClick != null;
        const dot = (
          <div className={classNames(styles.mobileDot, styles[`circle_${state}`])}>
            {state === 'completed' ? (
              <CheckIcon className={classNames(styles.checkIcon, styles.checkIconSmall)} />
            ) : (
              step
            )}
          </div>
        );
        return (
          <Fragment key={step}>
            {isClickable ? (
              <button
                type="button"
                className={styles.mobileDotButton}
                onClick={() => {
                  onStepClick(step);
                }}
              >
                {dot}
              </button>
            ) : (
              dot
            )}
            {!isLast && <div className={styles.mobileDivider} aria-hidden />}
          </Fragment>
        );
      })}
    </div>
  </nav>
);
